<script>
import date from 'locutus/php/datetime/date'
import defaults from 'lodash/defaults'
import {
  fetchForm,
  createForm,
  updateForm,
  fetchDocuments,
  fetchFormDocuments,
  fetchFormDocument,
  createFormDocument,
  deleteFormDocument,
} from '@apis/admin/contract'
import LangItem from '@components/admin/lang-input'
import { mapState } from 'vuex'
import fromPairs from 'lodash/fromPairs'

export default {
  components: { LangItem },
  props: {
    form_seq: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      params: {},
      info: null,
      documents: [],
      form_documents: [],
      document_params: null,
      loading: false,
      active_tab: 'info',
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.loading = true
      Promise.all([this.loadForm(), this.loadDocuments()])
        .then(() => this.loadFormDocuments())
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadForm() {
      const { form_info } = await fetchForm(this.form_seq)
      this.params = defaults(form_info, {
        name: '',
        title: '',
        seq: null,
        regdate: null,
      })
      this.info = form_info
    },
    async loadDocuments() {
      const { data } = await fetchDocuments()

      this.documents = data
    },
    async loadFormDocuments() {
      if (!this.info) return []

      const { data } = await fetchFormDocuments(this.info.seq)

      this.form_documents = data
    },
    async loadFormDocument(document_seq) {
      const { data } = await fetchFormDocument(this.info.seq, document_seq)

      this.document_params = data
    },
    handleSubmit() {
      const createOrUpdate = this.params.seq ? updateForm : createForm
      const confirm_message = this.params.seq
        ? this.$t('admin.msg_save')
        : this.$t('admin.msg_enroll')
      const success_message = this.params.seq
        ? this.$t('admin.msg_form_modify')
        : this.$t('admin.msg_form_enroll')

      this.loading = true
      this.$confirm(confirm_message, this.$t('admin.modal_confirm'))
        .then(() => createOrUpdate(this.params))
        .then(({ seq }) => this.$emit('submit', seq))
        .then(() => this.$toasted.success(success_message))
        .catch((e) => (e ? this.$toasted.error(e.message) : null))
        .finally(() => {
          this.loading = false
        })
    },
    handleAddDocument() {
      this.form_documents.push({
        form_seq: this.info.seq,
        document_seq: undefined,
        is_agree_required: 'N',
      })
    },
    async handleDeleteDocument(idx) {
      this.loading = true
      try {
        if (this.form_documents[idx].seq) {
          if (
            !(await this.$confirm(
              this.$t('admin.confirm_delete'),
              this.$t('admin.modal_confirm'),
              {
                type: 'warning',
              }
            ).catch(() => false))
          )
            return

          await deleteFormDocument(this.info.seq, this.form_documents[idx].seq)
        }

        this.$notify({
          title: this.$t('toasted_msg.title'),
          message: this.$t('admin.msg_delete'),
          type: 'success',
        })
        this.form_documents.splice(idx, 1)
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    handleSubmitDocument(idx) {
      this.loading = true
      const params = this.form_documents[idx]
      createFormDocument(params)
        .then(({ seq }) => fetchFormDocument(this.info.seq, seq))
        .then(({ document_info }) => {
          this.$notify({
            title: this.$t('toasted_msg.title'),
            message: params.seq
              ? this.$t('admin.msg_save_success')
              : this.$t('admin.msg_enroll_success'),
            type: 'success',
          })
          this.$set(this.form_documents, idx, { ...document_info })
        })
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    date,
    fromPairs,
  },
}
</script>

<template>
  <el-tabs v-model="active_tab" :stretch="true">
    <el-tab-pane :label="$t('admin.default_info')" name="info">
      <form
        v-loading="loading"
        action="./"
        method="get"
        @submit.prevent="handleSubmit"
      >
        <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
          <colgroup>
            <col width="20%" />
            <col width="30%" />
            <col width="20%" />
            <col width="30%" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="row"
                ><div>{{ $t('contract_form_info.name') }}</div></th
              >
              <td>
                <template v-if="params.seq">{{ params.name }}</template>
                <input
                  v-else
                  v-model="params.name"
                  type="text"
                  :class="[$style.inputTypeText]"
                />
              </td>
              <th scope="row"
                ><div>{{ $t('contract_form_info.title') }}</div></th
              >
              <td
                ><LangItem v-if="!loading" v-model="params.title"></LangItem
              ></td>
            </tr>
            <tr>
              <th colspan="4" :class="$style.button">
                <span :class="[$style.button, $style.black]"
                  ><input
                    type="submit"
                    :value="params.seq ? $t('admin.save') : $t('admin.enroll')"
                /></span>
              </th>
            </tr>
          </tbody>
        </table>
      </form>
    </el-tab-pane>
    <el-tab-pane
      :disabled="!info || !info.seq"
      :label="$t('contract_form_info.contract_document')"
      name="CONDITION"
    >
      <table
        v-if="info && info.seq"
        cellspacing="0"
        cellpadding="0"
        :class="$style.colTable"
      >
        <thead>
          <tr>
            <th scope="col">
              <div>
                {{ $t('contract_form_info.contract_document') }}
              </div>
            </th>
            <th scope="col">
              <div :style="[$style.nowrap]">
                {{ $t('contract_form_info.required_consent') }}
              </div>
            </th>
            <th scope="col">
              <div :class="$style.tRight">
                <a
                  href="#"
                  :class="[$style.black, $style.button]"
                  @click="handleAddDocument"
                  ><span>{{ $t('admin.add') }}</span></a
                >
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(form_document, idx) in form_documents"
            :key="form_document.seq ? `${form_document.seq}` : `create-${idx}`"
          >
            <td :class="$style.wide">
              <select
                v-model="form_document.document_seq"
                :disabled="form_document.seq"
              >
                <option :value="undefined">
                  {{ $t('contract_form_info.select') }}</option
                >
                <option
                  v-for="val in documents"
                  :key="val.seq"
                  :value="val.seq"
                >
                  {{ val.title }}
                </option>
              </select>
            </td>
            <td :class="[$style.tCenter, $style.nowrap]">
              <template v-if="form_document.seq">
                {{
                  form_document.is_agree_required === 'Y'
                    ? $t('contract_form_info.required')
                    : $t('contract_form_info.select')
                }}
              </template>
              <label v-else
                ><input
                  v-model="form_document.is_agree_required"
                  type="checkbox"
                  true-value="Y"
                  false-value="N"
                />
                {{ $t('contract_form_info.is_agree_required') }}</label
              >
            </td>
            <td :class="[$style.tRight, $style.nowrap]">
              <a
                v-if="!form_document.seq"
                href="#"
                :class="[$style.black, $style.button]"
                @click="handleSubmitDocument(idx)"
                ><span>{{ $t('admin.enroll') }}</span></a
              >
              {{ ' ' }}
              <a
                href="#"
                :class="[$style.red, $style.button]"
                @click="handleDeleteDocument(idx)"
                ><span>{{ $t('admin.delete') }}</span></a
              >
            </td>
          </tr>
          <tr v-if="!form_documents.length">
            <td :class="$style.tCenter" colspan="3">{{
              $t('contract_form_info.msg_contract_document_no')
            }}</td>
          </tr>
        </tbody>
      </table>
    </el-tab-pane>
  </el-tabs>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
